import { useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { parseExif, type ExifData } from "@/lib/intel/exif";
import { analyzeImageIntel, matchPersonPhoto, type ImageIntelReport, type PersonMatchReport } from "@/lib/intel/image.functions";
import { Button } from "@/components/ui/button";
import { getPaddleEnvironment } from "@/lib/paddle";
import {
  Upload, Loader2, MapPin, Tag, ScanText, ShieldAlert, Camera, Link2, Images, Compass, X, UserSearch,
} from "lucide-react";

const MAX_BYTES = 6 * 1024 * 1024;
const SUPPORTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

const VERDICT_TONE: Record<string, string> = {
  likely_authentic: "text-success border-success/40 bg-success/10",
  inconclusive: "text-muted-foreground border-panel-border bg-panel/40",
  possible_edit: "text-amber border-amber/40 bg-amber/10",
  likely_manipulated: "text-danger border-danger/40 bg-danger/10",
};

function Section({ icon: Icon, title, children }: { icon: typeof Tag; title: string; children: React.ReactNode }) {
  return (
    <section className="glass rounded-xl p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-cyan" />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">{title}</div>
      </div>
      <div className="mt-3 text-sm">{children}</div>
    </section>
  );
}

function ForensicPanel() {
  const analyze = useServerFn(analyzeImageIntel);
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [exif, setExif] = useState<ExifData | null>(null);
  const [sourceUrl, setSourceUrl] = useState("");
  const [context, setContext] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: async (): Promise<ImageIntelReport> => {
      if (!preview) throw new Error("Upload an image first");
      return analyze({
        data: {
          imageDataUrl: preview,
          fileName: fileName || undefined,
          sourceUrl: sourceUrl.trim() ? sourceUrl.trim() : undefined,
          exif: exif?.raw as Record<string, string | number> | undefined,
          gps: exif?.gps ? { lat: exif.gps.lat, lon: exif.gps.lon } : undefined,
          context: context.trim() ? context.trim() : undefined,
          environment: getPaddleEnvironment(),
        },
      });
    },
  });

  async function onFile(file: File) {
    setLocalError(null);
    run.reset();
    if (!SUPPORTED_TYPES.has(file.type)) { setLocalError("Use a JPEG, PNG, or WebP image."); return; }
    if (file.size > MAX_BYTES) { setLocalError("Image is larger than 6MB — resize and retry."); return; }
    const buf = await file.arrayBuffer();
    setExif(parseExif(buf));
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setPreview(String(reader.result));
    reader.readAsDataURL(file);
  }

  const report = run.data;

  return (
    <div className="space-y-3">
      <section className="glass rounded-xl p-4">
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f) void onFile(f);
          }}
          onClick={() => fileRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-panel-border bg-background/40 px-4 py-8 text-center hover:border-cyan/40"
        >
          {preview ? (
            <img src={preview} alt={fileName || "Uploaded image for analysis"} className="max-h-64 rounded-md object-contain" />
          ) : (
            <>
              <Upload className="h-5 w-5 text-cyan" />
              <div className="mt-2 text-sm">Drop an image here or click to upload</div>
              <div className="text-xs text-muted-foreground">JPEG, PNG, WebP · max 6MB · EXIF is read locally in your browser</div>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }}
          />
        </div>

        {preview && (
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span className="truncate">{fileName}</span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => { setPreview(null); setExif(null); setFileName(""); setSourceUrl(""); setContext(""); setLocalError(null); if (fileRef.current) fileRef.current.value = ""; run.reset(); }}
              className="h-7 px-2 hover:text-danger"
            >
              <X className="h-3 w-3" /> Clear
            </Button>
          </div>
        )}

        <div className="mt-3 grid gap-2 md:grid-cols-2">
          <input
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="Public image URL (optional — enables reverse search links)"
            className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm outline-none focus:border-cyan/40"
          />
          <input
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Analyst context (optional — e.g. claimed to be Berlin, 2023)"
            className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm outline-none focus:border-cyan/40"
          />
        </div>

        {(localError || run.error) && (
          <div className="mt-2 text-xs text-danger">{localError ?? (run.error as Error).message}</div>
        )}

        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            onClick={() => run.mutate()}
            disabled={!preview || run.isPending}
          >
            {run.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanText className="h-3.5 w-3.5" />}
            {run.isPending ? "Analysing image…" : "Run image intelligence"}
          </Button>
        </div>
      </section>

      {exif && (
        <Section icon={Camera} title="EXIF metadata (local)">
          {exif.gps && (
            <div className="mb-2 rounded border border-amber/40 bg-amber/10 p-2 text-xs text-amber">
              GPS coordinates embedded: {exif.gps.lat}, {exif.gps.lon} ·{" "}
              <a
                className="underline"
                target="_blank"
                rel="noreferrer"
                href={`https://www.openstreetmap.org/?mlat=${exif.gps.lat}&mlon=${exif.gps.lon}#map=15/${exif.gps.lat}/${exif.gps.lon}`}
              >
                open map
              </a>
            </div>
          )}
          <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
            {Object.entries(exif.raw).map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="font-mono uppercase text-muted-foreground">{k}</dt>
                <dd className="truncate">{String(v)}</dd>
              </div>
            ))}
          </dl>
        </Section>
      )}
      {preview && !exif && (
        <div className="glass rounded-xl p-4 text-xs text-muted-foreground">
          No EXIF metadata found — most social platforms strip it on upload, so absence is not evidence of editing.
        </div>
      )}

      {report && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-success/40 bg-success/10 p-3 text-xs">
            <span className="text-success">Analysis saved automatically to your Vault.</span>
            {report.scanRunId && (
              <Button asChild size="sm" variant="outline">
                <Link to="/vault/$scanId" params={{ scanId: report.scanRunId }}>
                  Open case
                </Link>
              </Button>
            )}
          </div>
          <Section icon={ScanText} title="Scene analysis">
            <p className="text-sm">{report.description || "No description returned."}</p>
            {report.objects.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {report.objects.map((o, i) => (
                  <span key={i} className="rounded border border-panel-border bg-background/40 px-2 py-0.5 text-xs">{o}</span>
                ))}
              </div>
            )}
          </Section>

          <div className="grid gap-3 lg:grid-cols-2">
            <Section icon={Compass} title="Landmarks">
              {report.landmarks.length ? (
                <ul className="space-y-2">
                  {report.landmarks.map((l, i) => (
                    <li key={i} className="rounded border border-panel-border bg-background/40 p-2">
                      <div className="flex justify-between gap-2">
                        <span className="font-medium">{l.name}</span>
                        <span className="font-mono text-[10px] text-cyan">{l.confidence}%</span>
                      </div>
                      <div className="text-xs text-muted-foreground">{l.detail}</div>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-xs text-muted-foreground">No landmarks identified.</div>}
            </Section>

            <Section icon={Tag} title="Logos & brands">
              {report.logos.length ? (
                <ul className="space-y-2">
                  {report.logos.map((l, i) => (
                    <li key={i} className="rounded border border-panel-border bg-background/40 p-2">
                      <div className="flex justify-between gap-2">
                        <span className="font-medium">{l.brand}</span>
                        <span className="font-mono text-[10px] text-cyan">{l.confidence}%</span>
                      </div>
                      <div className="text-xs text-muted-foreground">{l.detail}</div>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-xs text-muted-foreground">No logos detected.</div>}
            </Section>

            <Section icon={MapPin} title="Possible location">
              {report.locationGuess.length ? (
                <ul className="space-y-2">
                  {report.locationGuess.map((l, i) => (
                    <li key={i} className="rounded border border-panel-border bg-background/40 p-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium">{l.place}</span>
                        <span className="flex items-center gap-2">
                          <span
                            className={`rounded px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${
                              l.verified ? "bg-cyan/15 text-cyan" : "bg-muted/40 text-muted-foreground"
                            }`}
                          >
                            {l.verified ? "Gazetteer verified" : "Unverified guess"}
                          </span>
                          <span className="font-mono text-[10px] text-cyan">{l.confidence}%</span>
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">{l.rationale}</div>
                      {l.verifiedName && (
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          Resolved as <span className="text-foreground">{l.verifiedName}</span>
                          {l.country ? ` · ${l.country}` : ""}
                        </div>
                      )}
                      <div className="mt-1 flex flex-wrap gap-3">
                        {typeof l.lat === "number" && typeof l.lon === "number" && l.lat !== 0 && (
                          <a
                            className="inline-block text-[10px] font-mono uppercase tracking-widest text-cyan hover:underline"
                            target="_blank"
                            rel="noreferrer"
                            href={`https://www.openstreetmap.org/?mlat=${l.lat}&mlon=${l.lon}#map=13/${l.lat}/${l.lon}`}
                          >
                            View on map
                          </a>
                        )}
                        {(l.sources ?? []).map((s, si) => (
                          <a
                            key={si}
                            className="inline-block text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-cyan hover:underline"
                            target="_blank"
                            rel="noreferrer"
                            href={s.url}
                          >
                            {s.label}
                          </a>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-xs text-muted-foreground">Not enough geolocation clues.</div>}
              {report.geo && (report.geo.nearby.length > 0 || report.geo.elevationM != null || report.geo.exifLocation) && (
                <div className="mt-3 rounded border border-panel-border bg-background/40 p-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Gazetteer grounding</div>
                  {report.geo.exifLocation?.displayName && (
                    <div className="mt-1 text-xs">
                      EXIF GPS resolved to <span className="text-foreground">{report.geo.exifLocation.displayName}</span>
                    </div>
                  )}
                  {report.geo.elevationM != null && (
                    <div className="mt-1 text-xs text-muted-foreground">Elevation ≈ {Math.round(report.geo.elevationM)} m</div>
                  )}
                  {report.geo.nearby.length > 0 && (
                    <ul className="mt-1 space-y-1">
                      {report.geo.nearby.slice(0, 8).map((n, i) => (
                        <li key={i} className="text-xs text-muted-foreground">
                          {n.url ? (
                            <a href={n.url} target="_blank" rel="noreferrer" className="text-cyan hover:underline">{n.name}</a>
                          ) : n.name}
                          {" · "}{Math.round(n.distanceM)} m{n.kind ? ` · ${n.kind}` : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                  {report.geo.endpointsUsed.length > 0 && (
                    <div className="mt-2 font-mono text-[10px] text-muted-foreground">
                      Sources: {report.geo.endpointsUsed.join(" · ")}
                    </div>
                  )}
                </div>
              )}

            </Section>

            <Section icon={ShieldAlert} title="Manipulation analysis">
              <div className={`inline-flex rounded border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${VERDICT_TONE[report.manipulation.verdict] ?? VERDICT_TONE.inconclusive}`}>
                {report.manipulation.verdict.replace(/_/g, " ")} · {report.manipulation.confidence}%
              </div>
              {report.manipulation.notes && <p className="mt-2 text-xs text-muted-foreground">{report.manipulation.notes}</p>}
              <ul className="mt-2 space-y-1 text-xs">
                {report.manipulation.regions.map((r, i) => (
                  <li key={i} className="rounded border border-panel-border bg-background/40 p-2">
                    <span className="font-medium">{r.area}</span> — <span className="text-muted-foreground">{r.reason}</span>
                  </li>
                ))}
              </ul>
            </Section>
          </div>

          <Section icon={ScanText} title="Extracted text (OCR)">
            {report.ocrText ? (
              <pre className="whitespace-pre-wrap rounded border border-panel-border bg-background/40 p-2 text-xs">{report.ocrText}</pre>
            ) : <div className="text-xs text-muted-foreground">No readable text detected.</div>}
          </Section>

          <Section icon={Images} title="Similar images & corroboration">
            {report.similar.length ? (
              <ul className="space-y-2">
                {report.similar.map((s, i) => (
                  <li key={i} className="rounded border border-panel-border bg-background/40 p-2">
                    <a href={s.url} target="_blank" rel="noreferrer" className="font-medium text-cyan hover:underline">{s.title}</a>
                    <div className="text-xs text-muted-foreground">{s.note}</div>
                  </li>
                ))}
              </ul>
            ) : <div className="text-xs text-muted-foreground">No corroborating pages found.</div>}
          </Section>

          <Section icon={Link2} title="Reverse image search">
            {report.reverseSearch.length ? (
              <div className="flex flex-wrap gap-2">
                {report.reverseSearch.map((r) => (
                  <a
                    key={r.engine}
                    href={r.url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-md border border-cyan/40 bg-cyan/10 px-3 py-1.5 text-xs text-cyan hover:bg-cyan/20"
                  >
                    {r.engine}
                  </a>
                ))}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                Paste a public image URL above to generate one-click Google Lens, Yandex, Bing and TinEye reverse searches.
              </div>
            )}
          </Section>

          {report.pivots.length > 0 && (
            <Section icon={Compass} title="Suggested pivots">
              <ul className="space-y-1 text-xs">
                {report.pivots.map((p, i) => (
                  <li key={i} className="rounded border border-panel-border bg-background/40 p-2">
                    <span className="font-mono uppercase text-muted-foreground">{p.type}</span>{" "}
                    <span className="font-medium">{p.target}</span>
                    <div className="text-muted-foreground">{p.reason}</div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {report.citations.length > 0 && (
            <Section icon={Link2} title="Citations">
              <ul className="space-y-1 text-xs">
                {report.citations.map((c) => (
                  <li key={c}>
                    <a href={c} target="_blank" rel="noreferrer" className="text-cyan hover:underline break-all">{c}</a>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
// ---------------------------------------------------------------------------
// Person matching — upload a photo of a person and search for who it may be.
// ---------------------------------------------------------------------------

function confidenceTone(c: number) {
  if (c >= 70) return "text-success border-success/40 bg-success/10";
  if (c >= 40) return "text-amber border-amber/40 bg-amber/10";
  return "text-muted-foreground border-panel-border bg-panel/40";
}

function PersonMatchPanel() {
  const match = useServerFn(matchPersonPhoto);
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [hints, setHints] = useState({ name: "", employer: "", country: "", platform: "", context: "" });
  const [localError, setLocalError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: async (): Promise<PersonMatchReport> => {
      if (!preview) throw new Error("Upload a photo first");
      const cleaned = Object.fromEntries(
        Object.entries(hints).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()]),
      );
      return match({
        data: {
          imageDataUrl: preview,
          fileName: fileName || undefined,
          sourceUrl: sourceUrl.trim() ? sourceUrl.trim() : undefined,
          hints: Object.keys(cleaned).length ? cleaned : undefined,
          environment: getPaddleEnvironment(),
        },
      });
    },
  });

  async function onFile(file: File) {
    setLocalError(null);
    run.reset();
    if (!SUPPORTED_TYPES.has(file.type)) { setLocalError("Use a JPEG, PNG, or WebP image."); return; }
    if (file.size > MAX_BYTES) { setLocalError("Image is larger than 6MB — resize and retry."); return; }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setPreview(String(reader.result));
    reader.readAsDataURL(file);
  }

  const report = run.data;

  return (
    <div className="space-y-3">
      <section className="glass rounded-xl p-4">
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) void onFile(f); }}
          onClick={() => fileRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-panel-border bg-background/40 px-4 py-8 text-center hover:border-cyan/40"
        >
          {preview ? (
            <img src={preview} alt={fileName || "Photo of a person to match"} className="max-h-64 rounded-md object-contain" />
          ) : (
            <>
              <UserSearch className="h-5 w-5 text-cyan" />
              <div className="mt-2 text-sm">Drop a photo of a person here or click to upload</div>
              <div className="text-xs text-muted-foreground">JPEG, PNG, WebP · max 6MB</div>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }}
          />
        </div>

        {preview && (
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span className="truncate">{fileName}</span>
            <Button type="button" size="sm" variant="ghost" className="h-7 px-2 hover:text-danger"
              onClick={() => { setPreview(null); setFileName(""); setSourceUrl(""); setLocalError(null); if (fileRef.current) fileRef.current.value = ""; run.reset(); }}>
              <X className="h-3 w-3" /> Clear
            </Button>
          </div>
        )}

        <div className="mt-3 grid gap-2 md:grid-cols-2">
          <input value={hints.name} onChange={(e) => setHints({ ...hints, name: e.target.value })}
            placeholder="Suspected name (optional)"
            className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm outline-none focus:border-cyan/40" />
          <input value={hints.employer} onChange={(e) => setHints({ ...hints, employer: e.target.value })}
            placeholder="Employer / organisation (optional)"
            className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm outline-none focus:border-cyan/40" />
          <input value={hints.country} onChange={(e) => setHints({ ...hints, country: e.target.value })}
            placeholder="Country / region (optional)"
            className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm outline-none focus:border-cyan/40" />
          <input value={hints.platform} onChange={(e) => setHints({ ...hints, platform: e.target.value })}
            placeholder="Where the photo came from, e.g. LinkedIn (optional)"
            className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm outline-none focus:border-cyan/40" />
          <input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="Public image URL (optional — enables reverse search)"
            className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm outline-none focus:border-cyan/40" />
          <input value={hints.context} onChange={(e) => setHints({ ...hints, context: e.target.value })}
            placeholder="Analyst context (optional)"
            className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm outline-none focus:border-cyan/40" />
        </div>

        {(localError || run.error) && (
          <div className="mt-2 text-xs text-danger">{localError ?? (run.error as Error).message}</div>
        )}

        <div className="mt-3 flex justify-end">
          <Button type="button" onClick={() => run.mutate()} disabled={!preview || run.isPending}>
            {run.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserSearch className="h-3.5 w-3.5" />}
            {run.isPending ? "Searching for a match…" : "Match this person"}
          </Button>
        </div>
      </section>

      {report && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-success/40 bg-success/10 p-3 text-xs">
            <span className="text-success">Match report saved automatically to your Vault.</span>
            {report.scanRunId && (
              <Button asChild size="sm" variant="outline">
                <Link to="/vault/$scanId" params={{ scanId: report.scanRunId }}>Open case</Link>
              </Button>
            )}
          </div>

          <div className="rounded-lg border border-amber/40 bg-amber/10 p-3 text-xs text-amber">{report.caveat}</div>

          {(report.searchQueries.length > 0 || report.note) && (
            <Section icon={Link2} title="What was searched">
              {report.note && <p className="text-sm text-muted-foreground">{report.note}</p>}
              {report.searchQueries.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {report.searchQueries.map((q, i) => (
                    <span key={i} className="rounded border border-panel-border bg-panel/40 px-2 py-0.5 font-mono text-[11px] break-all">{q}</span>
                  ))}
                </div>
              )}
              {report.webHits.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-cyan">{report.webHits.length} page(s) retrieved and reviewed</summary>
                  <ul className="mt-2 space-y-1 text-xs">
                    {report.webHits.map((h, i) => (
                      <li key={i}>
                        <a href={h.url} target="_blank" rel="noreferrer" className="text-cyan underline break-all">{h.title}</a>
                        {h.domain && <span className="ml-1 font-mono text-[10px] text-muted-foreground">({h.domain})</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {(report.endpointsUsed ?? []).length > 0 && (
                <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Endpoints queried: {report.endpointsUsed.join(" · ")}
                </div>
              )}

            </Section>
          )}

          <Section icon={UserSearch} title="Candidate identities">
            {report.candidates.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No candidate could be tied to this photo by citable evidence. Matching works from readable text and open-source
                search, not face biometrics — add a suspected name, employer, country or a public image URL and retry, or use the
                reverse image search links below.
              </p>

            ) : (
              <ul className="space-y-3">
                {report.candidates.map((c, i) => (
                  <li key={i} className="rounded-lg border border-panel-border bg-background/40 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium">{c.name}</div>
                        {c.role && <div className="text-xs text-muted-foreground">{c.role}</div>}
                      </div>
                      <div className="text-right">
                        <span className={`rounded border px-2 py-0.5 font-mono text-[10px] ${confidenceTone(c.confidence)}`}>
                          {c.confidence}% evidence-weighted match
                        </span>
                        <div className="mt-1 font-mono text-[9px] uppercase text-muted-foreground">
                          {c.verification} · {c.independentSources} independent source(s)
                          {c.textualNameEvidence ? " · name legible in image" : " · appearance-only lead"}
                        </div>
                      </div>
                    </div>
                    {!c.textualNameEvidence && (
                      <div className="mt-2 rounded border border-amber/30 bg-amber/10 px-2 py-1 text-[11px] text-amber">
                        No name was readable in the image itself, so this candidate is capped as a lead requiring human verification.
                      </div>
                    )}
                    {c.rationale && <p className="mt-2 text-xs text-muted-foreground">{c.rationale}</p>}
                    {c.matchedSignals.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {c.matchedSignals.map((s, j) => (
                          <span key={j} className="rounded border border-panel-border bg-panel/40 px-2 py-0.5 text-[11px]">{s}</span>
                        ))}
                      </div>
                    )}
                    {c.sources.length > 0 && (
                      <ul className="mt-2 space-y-1 text-xs">
                        {c.sources.map((s, j) => (
                          <li key={j}>
                            <a href={s.url} target="_blank" rel="noreferrer" className="text-cyan underline break-all">{s.title}</a>
                            {s.domain && <span className="ml-1 font-mono text-[10px] text-muted-foreground">({s.domain})</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                    {(c.registry ?? []).length > 0 && (
                      <div className="mt-2 rounded border border-panel-border bg-panel/30 p-2">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                          Public registry corroboration
                        </div>
                        <ul className="mt-1 space-y-1 text-xs">
                          {c.registry.map((r, j) => (
                            <li key={j}>
                              <span className="font-mono text-[10px] text-cyan">{r.provider}</span>{" "}
                              <a href={r.url} target="_blank" rel="noreferrer" className="underline break-all">{r.name}</a>
                              {r.description && <span className="text-muted-foreground"> · {r.description}</span>}
                              {r.attributes.length > 0 && (
                                <span className="text-muted-foreground"> · {r.attributes.join("; ")}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {c.suggestedScanTarget && (
                      <div className="mt-3">
                        <Button asChild size="sm" variant="outline">
                          <Link to="/dashboard" search={{ q: c.suggestedScanTarget }}>Investigate {c.suggestedScanTarget}</Link>
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {report.aliasClusters.length > 0 && (
            <Section icon={UserSearch} title="Possible alias links">
              <p className="mb-2 text-xs text-muted-foreground">
                These candidate names share sources or name parts. They may be aliases of one person — or the same photo
                reused across unrelated profiles. Verify before merging.
              </p>
              <ul className="space-y-2">
                {report.aliasClusters.map((cluster) => (
                  <li key={cluster.id} className="rounded-lg border border-panel-border bg-background/40 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm font-medium">{cluster.labels.join("  ·  ")}</div>
                      <span className={`rounded border px-2 py-0.5 font-mono text-[10px] ${confidenceTone(cluster.confidence)}`}>
                        {cluster.confidence}% link strength
                      </span>
                    </div>
                    <ul className="mt-1 text-xs text-muted-foreground">
                      {cluster.evidence.map((e) => <li key={e}>• {e}</li>)}
                    </ul>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section icon={ScanText} title="Visual descriptor">
            <p className="text-sm">{report.descriptor.appearance || "No description returned."}</p>
            <dl className="mt-2 grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
              <div className="flex gap-2"><dt className="font-mono uppercase text-muted-foreground">age</dt><dd>{report.descriptor.ageRange}</dd></div>
              <div className="flex gap-2"><dt className="font-mono uppercase text-muted-foreground">attire</dt><dd>{report.descriptor.attire || "—"}</dd></div>
              <div className="flex gap-2"><dt className="font-mono uppercase text-muted-foreground">setting</dt><dd>{report.descriptor.setting || "—"}</dd></div>
            </dl>
            {report.descriptor.distinguishingFeatures.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {report.descriptor.distinguishingFeatures.map((f, i) => (
                  <span key={i} className="rounded border border-panel-border bg-background/40 px-2 py-0.5 text-xs">{f}</span>
                ))}
              </div>
            )}
            {report.descriptor.visibleText.length > 0 && (
              <pre className="mt-2 whitespace-pre-wrap rounded bg-background/60 p-2 text-xs">{report.descriptor.visibleText.join("\n")}</pre>
            )}
          </Section>

          {report.reverseSearch.length > 0 && (
            <Section icon={Link2} title="Reverse image search">
              <div className="flex flex-wrap gap-2">
                {report.reverseSearch.map((r, i) => (
                  <a key={i} href={r.url} target="_blank" rel="noreferrer"
                    className="rounded border border-panel-border bg-background/40 px-2 py-1 text-xs text-cyan underline">{r.engine}</a>
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

export function ImageIntelPanel() {
  const [mode, setMode] = useState<"forensics" | "person">("forensics");
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {([
          { id: "forensics", label: "Forensic analysis", icon: ScanText },
          { id: "person", label: "Match a person", icon: UserSearch },
        ] as const).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setMode(t.id)}
            className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs ${
              mode === t.id ? "border-cyan/50 bg-cyan/10 text-cyan" : "border-panel-border bg-panel/40 text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>
      {mode === "forensics" ? <ForensicPanel /> : <PersonMatchPanel />}
    </div>
  );
}
